import type { DateTime } from "luxon";
import { formatPersonFullName } from "../../utils/formatters";
import { formatCurrency } from "../../utils/utilFunctions";
import { CandidatePipelineStatus, InterviewShift } from "./types";

/** Em dash for an absent value, so every read-only cell renders the same "nothing here" glyph. */
export const EMPTY_DASH = "—";

/** Accepted document upload types across the Hub (CV/resume + certificate) — kept in sync with the backend
 *  allowlist (_ALLOWED_DOCUMENT_EXTENSIONS). A resume may be a PDF/Word doc or a scan/screenshot image. */
export const HUB_DOC_ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png";

/** Serialize a Luxon date to the "yyyy-MM-dd" the Hub API expects; invalid/absent → null. */
export const toApiDate = (value: DateTime | null | undefined): string | null => (value?.isValid ? value.toFormat("yyyy-MM-dd") : null);

/** Full display name from first/last (falls back to the id when both are blank) — the interviewer/candidate
 *  label used across the grid, dropdowns, and dock. `lastNameFirst` is the department's name-order flag. */
export const personFullName = (parts: { first_name?: string | null; last_name?: string | null; user_id?: string | null }, lastNameFirst: boolean): string =>
	// DEFAULT: an absent part is blank (interviewer users have no last name); the trimmed result then falls back to the id.
	formatPersonFullName({ firstName: parts.first_name ?? "", lastName: parts.last_name ?? "" }, lastNameFirst) || parts.user_id || "";

/** First initials from first/last name, for an identity tile. Empty when there is no name to initial. */
export const personInitials = (parts: { first_name?: string | null; last_name?: string | null }): string =>
	[parts.first_name, parts.last_name]
		.filter(Boolean)
		.map((part) => (part as string).trim().charAt(0).toUpperCase())
		.join("");

/** i18n key for a pipeline stage label (shared by the count bar, filters, and the status-change modal). */
export const pipelineStatusLabelKey = (status: CandidatePipelineStatus): string => {
	const map: Record<CandidatePipelineStatus, string> = {
		[CandidatePipelineStatus.NEW]: "pipeline.status.chip.new",
		[CandidatePipelineStatus.SCREENED]: "pipeline.status.chip.screened",
		[CandidatePipelineStatus.INTERVIEWED]: "pipeline.status.chip.interviewed",
		[CandidatePipelineStatus.OFFERED]: "pipeline.status.chip.offered",
		[CandidatePipelineStatus.HIRED]: "pipeline.status.chip.hired",
		[CandidatePipelineStatus.ACCEPTED_NEVER_STARTED]: "pipeline.status.chip.accepted_never_started",
		[CandidatePipelineStatus.DISQUALIFIED]: "pipeline.status.chip.disqualified",
	};
	return map[status];
};

/** i18n key for a candidate's target shift. */
export const shiftLabelKey = (shift: InterviewShift | string): string => {
	switch (shift) {
		case InterviewShift.DAY:
			return "hiring_hub.shift.day";
		case InterviewShift.NIGHT:
			return "hiring_hub.shift.night";
		case InterviewShift.BOTH:
			return "hiring_hub.shift.both";
		default:
			return "hiring_hub.shift.unknown";
	}
};

/** Format a wage for read-only display; `null` → em dash. Coerces defensively in case the wire sends a
 *  numeric string for the backend `Decimal`. */
export const formatWage = (wage: number | null): string => {
	if (wage === null || wage === undefined) return EMPTY_DASH;
	const value = Number(wage);
	return Number.isNaN(value) ? EMPTY_DASH : formatCurrency(value);
};

/** Format a Luxon date (already converted by the axios interceptor); `null` → em dash. */
export const formatHubDate = (date: DateTime | null): string => (date?.isValid ? date.toFormat("MMM d, yyyy") : EMPTY_DASH);

/** Format a Luxon timestamp down to the minute ("Sep 1, 2026, 11:24 AM") — note authorship lines. */
export const formatHubDateTime = (date: DateTime | null): string => (date?.isValid ? date.toFormat("MMM d, yyyy, h:mm a") : EMPTY_DASH);

/** A wage input is valid when empty (optional) OR a non-negative number — the backend enforces `ge=0`, so
 *  the Add/Edit forms must block negatives inline instead of firing a rejected request. */
export const isValidWage = (value: string): boolean => {
	const trimmed = value.trim();
	if (trimmed.length === 0) return true;
	const parsed = Number(trimmed);
	return !Number.isNaN(parsed) && parsed >= 0;
};
